/**
 * Remote Desktop Client - Main Component
 * Orchestrates all services for remote desktop functionality
 */

import {
    SignalRService,
    WebRTCService,
    VideoDecoderService,
    InputService,
    AuthService,
    AudioService,
    FileTransferService,
    ClipboardService,
    EncryptionService,
    buildConfigFromIceServers
} from '../services';

import {
    AgentInfo,
    ConnectionStatus,
    SessionInfo,
    VideoCodec,
    FrameData,
    WebRTCSignal,
    WebRTCSignalType,
    InputCommand,
    VideoStats,
    WebRTCStats,
    ViewerSettings,
    QualityPreset,
    EncryptedFrame,
    KeyExchangeRequest,
    KeyExchangeResponse
} from '../types';

export interface RemoteDesktopClientConfig {
    serverUrl: string;
    canvas: HTMLCanvasElement;
    autoReconnect: boolean;
    enableP2P: boolean;
    enableEncryption: boolean;
}

export interface RemoteDesktopClientEvents {
    onConnectionStatusChange?: (status: ConnectionStatus) => void;
    onAgentListUpdated?: (agents: AgentInfo[]) => void;
    onSessionStarted?: (session: SessionInfo) => void;
    onSessionEnded?: (reason: string) => void;
    onVideoStatsUpdate?: (stats: VideoStats) => void;
    onNetworkStatsUpdate?: (stats: WebRTCStats) => void;
    onMonitorChanged?: (monitorIndex: number, width: number, height: number) => void;
    onError?: (error: Error) => void;
    onLog?: (message: string) => void;
}

const DEFAULT_CONFIG: Partial<RemoteDesktopClientConfig> = {
    autoReconnect: true,
    enableP2P: true,
    enableEncryption: true
};

export class RemoteDesktopClient {
    private config: RemoteDesktopClientConfig;
    private events: RemoteDesktopClientEvents;

    // Services
    private signalR: SignalRService;
    private webRTC: WebRTCService | null = null;
    private videoDecoder: VideoDecoderService;
    private inputService: InputService;
    private authService: AuthService;
    private audioService: AudioService;
    private fileTransferService: FileTransferService;
    private clipboardService: ClipboardService;
    private encryption: EncryptionService;

    // State
    private connectionStatus: ConnectionStatus = ConnectionStatus.Disconnected;
    private currentSession: SessionInfo | null = null;
    private isP2PConnected = false;
    private agents: AgentInfo[] = [];

    // BUG-6: Frame source state machine for P2P switchover
    private frameSource: 'relay' | 'p2p' | 'switching' = 'relay';

    // Diagnostic counters
    private _diag = { framesReceived: 0, framesDecoded: 0, decodeErrors: 0, lastError: '' };

    // Settings
    private settings: ViewerSettings = {
        quality: QualityPreset.Auto,
        fitToWindow: true,
        showToolbar: true,
        showStats: false,
        captureKeyboard: true,
        enableClipboard: true,
        enableAudio: false
    };

    constructor(config: RemoteDesktopClientConfig, events: RemoteDesktopClientEvents = {}) {
        this.config = { ...DEFAULT_CONFIG, ...config } as RemoteDesktopClientConfig;
        this.events = events;

        // Initialize services
        this.signalR = new SignalRService(config.serverUrl, '/remotedesktop', {
            onConnectionStateChange: this.handleConnectionStateChange.bind(this),
            onAgentListUpdated: this.handleAgentListUpdated.bind(this),
            onSessionStarted: this.handleSessionStarted.bind(this),
            onSessionEnded: this.handleSessionEnded.bind(this),
            onFrameReceived: this.handleFrameReceived.bind(this),
            onFrameReceivedEx: this.handleFrameReceivedEx.bind(this),
            onWebRTCSignal: this.handleWebRTCSignal.bind(this),
            onWebRTCConnectionEstablished: this.handleWebRTCEstablished.bind(this),
            onWebRTCConnectionFailed: this.handleWebRTCFailed.bind(this),
            // WC-1: Audio frames from agent → AudioService
            onAudioFrameReceived: (frame) => {
                this.audioService.processAudioFrame(frame);
            },
            // WC-2: Clipboard data from agent → ClipboardService
            onClipboardDataReceived: (data) => {
                this.clipboardService.handleRemoteClipboard(data);
            },
            // WC-3: File transfer events from agent → FileTransferService
            onFileChunkReceived: (chunk) => {
                this.fileTransferService.handleChunkReceived(chunk);
            },
            onFileTransferProgress: (progress) => {
                this.fileTransferService.handleTransferProgress(progress);
            },
            onFileTransferComplete: (complete) => {
                this.fileTransferService.handleTransferComplete(complete);
            },
            onBrowseDirectoryResponse: (response) => {
                this.fileTransferService.handleDirectoryResponse(response);
            },
            onMonitorChanged: (_sessionId, monitorIndex, width, height) => {
                this.events.onMonitorChanged?.(monitorIndex, width, height);
            },
            // E2E encryption: key exchange + encrypted frames
            onKeyExchangeRequest: this.handleKeyExchangeRequest.bind(this),
            onKeyExchangeResponse: this.handleKeyExchangeResponse.bind(this),
            onEncryptedFrameReceived: this.handleEncryptedFrameReceived.bind(this),
            onError: this.handleError.bind(this)
        });

        this.videoDecoder = new VideoDecoderService(config.canvas, {
            onStatsUpdate: this.handleVideoStatsUpdate.bind(this),
            onError: this.handleError.bind(this)
        });

        this.inputService = new InputService({
            canvas: config.canvas,
            remoteWidth: 1920,
            remoteHeight: 1080,
            captureKeyboard: this.settings.captureKeyboard,
            capturePointerLock: false
        }, {
            onInputCommand: this.handleInputCommand.bind(this)
        });

        this.authService = new AuthService({
            serverUrl: config.serverUrl,
            tokenRefreshThresholdMs: 60000 // Refresh 1 minute before expiry
        }, {
            onAuthStateChange: this.handleAuthStateChange.bind(this),
            onError: this.handleError.bind(this)
        });

        this.audioService = new AudioService({
            onError: (msg: string) => this.handleError(new Error(msg))
        });

        this.fileTransferService = new FileTransferService(this.signalR, {
            onError: (transferId: string, error: string) => this.handleError(new Error(`File transfer ${transferId} failed: ${error}`))
        });

        this.clipboardService = new ClipboardService(this.signalR, {
            onError: (msg: string) => this.handleError(new Error(msg))
        });

        this.encryption = new EncryptionService({
            onStateChanged: (state) => this.log(`Encryption state: ${state}`)
        });

        this.log('Remote Desktop Client initialized');
    }

    // =========================================================================
    // Public API
    // =========================================================================

    /**
     * Connect to the server
     */
    async connect(): Promise<void> {
        this.updateConnectionStatus(ConnectionStatus.Connecting);

        try {
            // Set auth token if available
            const token = this.authService.getAccessToken();
            if (token) {
                this.signalR.setAccessToken(token);
            }

            // Reconnect SignalR if already connected (e.g., after login with new token)
            if (this.signalR.isConnected) {
                await this.signalR.disconnect();
            }

            await this.signalR.connect();

            // Register as web client viewer (works without auth)
            await this.signalR.registerViewer({
                clientName: 'LEVOR Web Client',
                platform: this.detectPlatform()
            });

            // Get initial agent list (requires auth — may fail for unauthenticated viewers)
            try {
                this.agents = await this.signalR.getAgentList();
                this.events.onAgentListUpdated?.(this.agents);
            } catch {
                // Unauthenticated — agent list will be populated after login
                this.agents = [];
                this.log('Agent list unavailable — login required for account-based access');
            }

            this.updateConnectionStatus(ConnectionStatus.Connected);
            this.log('Connected to server');
        } catch (error) {
            this.updateConnectionStatus(ConnectionStatus.Error);
            throw error;
        }
    }

    /**
     * Disconnect from the server
     */
    async disconnect(): Promise<void> {
        if (this.currentSession) {
            await this.endSession();
        }

        await this.signalR.disconnect();
        this.updateConnectionStatus(ConnectionStatus.Disconnected);
        this.log('Disconnected from server');
    }

    /**
     * Login with credentials
     */
    async login(username: string, password: string): Promise<boolean> {
        return await this.authService.login({ username, password });
    }

    /**
     * Logout
     */
    async logout(): Promise<void> {
        await this.authService.logout();
        if (this.signalR.isConnected) {
            await this.disconnect();
        }
    }

    /**
     * Get list of available agents
     */
    getAgents(): AgentInfo[] {
        return this.agents;
    }

    /**
     * Start a remote desktop session with an agent
     */
    async startSession(agentId: string, monitorIndex: number = 0): Promise<void> {
        if (this.currentSession) {
            throw new Error('Session already active');
        }

        this.log(`Starting session with agent: ${agentId}`);

        try {
            const session = await this.signalR.requestSession(agentId);
            if (!session) {
                throw new Error('Failed to start session - agent may be busy');
            }
            this.currentSession = session;

            // Initialize video decoder
            const agent = this.agents.find(a => a.agentId === agentId);
            const monitor = agent?.monitors?.[monitorIndex];
            const width = monitor?.width || 1920;
            const height = monitor?.height || 1080;

            await this.videoDecoder.initialize({
                codec: VideoCodec.Jpeg, // Start with JPEG, may switch later
                width,
                height
            });

            // Update input service dimensions
            this.inputService.updateRemoteDimensions(width, height);

            // Start input capture
            this.inputService.startCapture();

            // WC-4: Set session ID on all services so they can communicate with server
            this.fileTransferService.setSessionId(session.sessionId);
            this.clipboardService.setSessionId(session.sessionId);
            // Initialize audio (requires user gesture — startSession is user-triggered)
            await this.audioService.initialize().catch(err =>
                this.log(`Audio initialization skipped: ${err}`)
            );

            // Initialize E2E encryption if enabled
            if (this.config.enableEncryption) {
                await this.initiateKeyExchange(session.sessionId);
            }

            // Initialize WebRTC for P2P if enabled
            if (this.config.enableP2P) {
                await this.initializeWebRTC(session.sessionId);
            }

            this.updateConnectionStatus(ConnectionStatus.Streaming);
            this.events.onSessionStarted?.(session);
            this.log(`Session started: ${session.sessionId}`);

            this._diag = { framesReceived: 0, framesDecoded: 0, decodeErrors: 0, lastError: '' };
        } catch (error) {
            this.log(`Failed to start session: ${error}`);
            throw error;
        }
    }

    /**
     * Start a session using Agent ID + Password (TeamViewer-style quick connect).
     * Does not require prior authentication — password validates server-side.
     */
    async startSessionWithCredentials(agentId: string, password: string, monitorIndex: number = 0): Promise<void> {
        if (this.currentSession) {
            throw new Error('Session already active');
        }

        this.log(`Starting session with credentials for agent: ${agentId}`);

        try {
            const session = await this.signalR.requestSessionWithCredentials(agentId, password);
            if (!session) {
                throw new Error('שגיאת התחברות - בדוק מזהה וסיסמה');
            }
            this.currentSession = session;

            // Initialize video decoder
            const agent = this.agents.find(a => a.agentId === agentId);
            const monitor = agent?.monitors?.[monitorIndex];
            const width = monitor?.width || 1920;
            const height = monitor?.height || 1080;

            await this.videoDecoder.initialize({
                codec: VideoCodec.Jpeg,
                width,
                height
            });

            this.inputService.updateRemoteDimensions(width, height);
            this.inputService.startCapture();

            // WC-4: Set session ID on all services
            this.fileTransferService.setSessionId(session.sessionId);
            this.clipboardService.setSessionId(session.sessionId);
            await this.audioService.initialize().catch(err =>
                this.log(`Audio initialization skipped: ${err}`)
            );

            // Initialize E2E encryption if enabled
            if (this.config.enableEncryption) {
                await this.initiateKeyExchange(session.sessionId);
            }

            if (this.config.enableP2P) {
                await this.initializeWebRTC(session.sessionId);
            }

            this.updateConnectionStatus(ConnectionStatus.Streaming);
            this.events.onSessionStarted?.(session);
            this.log(`Session started with credentials: ${session.sessionId}`);

            this._diag = { framesReceived: 0, framesDecoded: 0, decodeErrors: 0, lastError: '' };
        } catch (error) {
            this.log(`Failed to start session with credentials: ${error}`);
            throw error;
        }
    }

    /**
     * End the current session
     */
    async endSession(): Promise<void> {
        if (!this.currentSession) return;

        const sessionId = this.currentSession.sessionId;
        this.log(`Ending session: ${sessionId}`);

        // Stop input capture
        this.inputService.stopCapture();

        // Stop audio/video and reset service session IDs
        this.audioService.stop();
        this.videoDecoder.dispose();
        this.fileTransferService.setSessionId(null);
        this.clipboardService.setSessionId(null);
        this.encryption.reset();

        // Close WebRTC
        this.webRTC?.close();
        this.webRTC = null;
        this.isP2PConnected = false;
        this.frameSource = 'relay'; // BUG-6: Reset state

        // End session on server
        await this.signalR.endSession(sessionId);

        this.currentSession = null;
        this.updateConnectionStatus(ConnectionStatus.Connected);
        this.events.onSessionEnded?.('User ended session');
    }

    /**
     * Switch to a different monitor
     */
    async switchMonitor(monitorIndex: number): Promise<void> {
        if (!this.currentSession) return;

        await this.signalR.selectMonitor(this.currentSession.sessionId, monitorIndex);
    }

    /**
     * Update viewer settings
     */
    updateSettings(settings: Partial<ViewerSettings>): void {
        this.settings = { ...this.settings, ...settings };

        // Apply settings
        if (settings.captureKeyboard !== undefined) {
            if (settings.captureKeyboard) {
                this.inputService.startCapture();
            } else {
                this.inputService.stopCapture();
            }
        }

        // S2-6: Wire clipboard toggle to ClipboardService
        if (settings.enableClipboard !== undefined) {
            this.clipboardService?.setEnabled(settings.enableClipboard).catch(err =>
                console.warn('[Clipboard] Failed to update settings:', err)
            );
        }

        // S2-7: Wire audio toggle to AudioService
        if (settings.enableAudio !== undefined) {
            this.audioService?.setMuted(!settings.enableAudio);
        }
    }

    /**
     * Get current settings
     */
    getSettings(): ViewerSettings {
        return { ...this.settings };
    }

    /** Dispose all resources — call on page unload or component destroy */
    dispose(): void {
        this.audioService.dispose();
        this.videoDecoder.dispose();
        this.clipboardService.dispose();
        this.fileTransferService.dispose();
        this.webRTC?.close();
        this.webRTC = null;
    }

    /**
     * Get current connection status
     */
    getConnectionStatus(): ConnectionStatus {
        return this.connectionStatus;
    }

    /** Access AudioService for volume/mute control */
    getAudioService(): AudioService { return this.audioService; }

    /** Access FileTransferService for file operations */
    getFileTransferService(): FileTransferService { return this.fileTransferService; }

    /** Access ClipboardService for clipboard sync */
    getClipboardService(): ClipboardService { return this.clipboardService; }

    /**
     * Get current session info
     */
    getCurrentSession(): SessionInfo | null {
        return this.currentSession;
    }

    /**
     * Check if P2P is connected
     */
    get isUsingP2P(): boolean {
        return this.isP2PConnected;
    }

    /**
     * Check if authenticated
     */
    get isAuthenticated(): boolean {
        return this.authService.isAuthenticated;
    }

    // =========================================================================
    // Private Methods - Event Handlers
    // =========================================================================

    private handleConnectionStateChange(status: ConnectionStatus): void {
        this.log(`Connection state: ${status}`);

        if (status === ConnectionStatus.Reconnecting) {
            // Keep streaming status if we're just reconnecting
            if (this.connectionStatus === ConnectionStatus.Streaming) {
                return;
            }
        }

        this.updateConnectionStatus(status);
    }

    private handleAgentListUpdated(agents: AgentInfo[]): void {
        this.agents = agents;
        this.events.onAgentListUpdated?.(agents);
    }

    private handleSessionStarted(session: SessionInfo): void {
        // Already handled in startSession
    }

    private handleSessionEnded(sessionId: string, reason: string): void {
        if (this.currentSession?.sessionId !== sessionId) return;

        this.inputService.stopCapture();
        this.audioService.stop();
        this.videoDecoder.dispose();
        this.fileTransferService.setSessionId(null);
        this.clipboardService.setSessionId(null);
        this.encryption.reset();
        this.webRTC?.close();
        this.webRTC = null;
        this.currentSession = null;
        this.isP2PConnected = false;
        this.frameSource = 'relay'; // BUG-6: Reset state

        this.updateConnectionStatus(ConnectionStatus.Connected);
        this.events.onSessionEnded?.(reason);
        this.log(`Session ended: ${reason}`);
    }

    private async handleFrameReceived(sessionId: string, frameData: Uint8Array, width: number, height: number): Promise<void> {
        // BUG-6: Reject relay frames when P2P is active or switching
        if (this.frameSource !== 'relay') return;

        const frame: FrameData = {
            data: frameData,
            width,
            height,
            codec: VideoCodec.Jpeg, // SignalR relay uses JPEG
            isKeyFrame: true,
            frameNumber: 0,
            timestamp: performance.now()
        };

        try {
            await this.videoDecoder.decodeFrame(frame);
        } catch (error) {
            this._diag.decodeErrors++;
            this._diag.lastError = String(error);
        }
    }

    private async handleFrameReceivedEx(frameData: Uint8Array, width: number, height: number, codec: number, isKeyFrame: boolean): Promise<void> {
        // BUG-6: Reject relay frames when P2P is active or switching
        if (this.frameSource !== 'relay') return;

        this._diag.framesReceived++;

        const frame: FrameData = {
            data: frameData,
            width,
            height,
            codec: codec as VideoCodec,
            isKeyFrame,
            frameNumber: 0,
            timestamp: performance.now()
        };

        try {
            await this.videoDecoder.decodeFrame(frame);
            this._diag.framesDecoded++;
        } catch (error) {
            this._diag.decodeErrors++;
            this._diag.lastError = String(error);
        }
    }

    private async handleWebRTCSignal(sessionId: string, signal: WebRTCSignal): Promise<void> {
        if (!this.webRTC || sessionId !== this.currentSession?.sessionId) return;

        try {
            switch (signal.type) {
                case WebRTCSignalType.Offer:
                    // Perfect Negotiation: handle incoming offer (renegotiation or collision)
                    if (signal.sdp) {
                        const answer = await this.webRTC.processOffer(signal.sdp);
                        if (answer) {
                            await this.signalR.sendWebRTCSignal(sessionId, {
                                type: WebRTCSignalType.Answer,
                                sessionId,
                                sdp: answer,
                                sdpType: 'answer'
                            });
                        }
                        // null = collision, offer was ignored (impolite side)
                    }
                    break;

                case WebRTCSignalType.Answer:
                    if (signal.sdp) {
                        await this.webRTC.processAnswer(signal.sdp);
                    }
                    break;

                case WebRTCSignalType.IceCandidate:
                    if (signal.iceCandidate) {
                        await this.webRTC.addIceCandidate(signal.iceCandidate);
                    }
                    break;
            }
        } catch (error) {
            this.log(`WebRTC signal error: ${error}`);
        }
    }

    // HIGH-7: Handle server-confirmed WebRTC P2P established
    private handleWebRTCEstablished(sessionId: string): void {
        if (sessionId !== this.currentSession?.sessionId) return;
        this.log(`Server confirmed WebRTC P2P established for session ${sessionId}`);
    }

    // HIGH-7: Handle server-reported WebRTC P2P failure
    private handleWebRTCFailed(sessionId: string, reason: string): void {
        if (sessionId !== this.currentSession?.sessionId) return;
        this.log(`Server reported WebRTC failure: ${reason}`);
        // If we were in P2P mode, fall back to relay
        if (this.frameSource !== 'relay') {
            this.frameSource = 'relay';
            this.isP2PConnected = false;
            this.log('Falling back to SignalR relay due to server-reported failure');
        }
    }

    private handleInputCommand(command: InputCommand): void {
        if (!this.currentSession) return;

        // Try P2P first
        if (this.isP2PConnected && this.webRTC) {
            const json = JSON.stringify(command);
            if (this.webRTC.sendInput(json)) {
                return; // Sent via P2P
            }
        }

        // Use E2E encryption if established
        if (this.encryption.isSecure) {
            const encrypted = this.encryption.encryptInputCommand(command);
            this.signalR.sendEncryptedInput(this.currentSession.sessionId, encrypted);
            return;
        }

        // Fall back to plaintext SignalR
        this.signalR.sendInput(this.currentSession.sessionId, command);
    }

    // =========================================================================
    // Private Methods - E2E Encryption
    // =========================================================================

    /**
     * Initiate key exchange with the agent for E2E encryption.
     * Non-blocking: encryption failure doesn't prevent the session from working.
     */
    private async initiateKeyExchange(sessionId: string): Promise<void> {
        try {
            await this.encryption.initialize();
            const request = this.encryption.createKeyExchangeRequest();
            await this.signalR.initiateKeyExchange(sessionId, request);
            this.log('Key exchange initiated');
        } catch (err) {
            this.log(`Key exchange initiation failed (session continues unencrypted): ${err}`);
        }
    }

    /**
     * Handle key exchange request from agent (responder role).
     */
    private async handleKeyExchangeRequest(sessionId: string, request: KeyExchangeRequest): Promise<void> {
        if (sessionId !== this.currentSession?.sessionId) return;

        try {
            if (!this.encryption.state || this.encryption.state === 'none') {
                await this.encryption.initialize();
            }
            const response = this.encryption.processKeyExchangeRequest(request);
            await this.signalR.sendKeyExchangeResponse(sessionId, response);
            this.log(`Key exchange response sent (accepted: ${response.accepted})`);
        } catch (err) {
            this.log(`Key exchange request handling failed: ${err}`);
        }
    }

    /**
     * Handle key exchange response from agent (initiator role).
     */
    private handleKeyExchangeResponse(sessionId: string, response: KeyExchangeResponse): void {
        if (sessionId !== this.currentSession?.sessionId) return;

        const success = this.encryption.processKeyExchangeResponse(response);
        if (success) {
            this.log('E2E encryption established');
        } else {
            this.log('E2E encryption failed — session continues with TLS only');
        }
    }

    /**
     * Handle encrypted frames from agent. Decrypt and decode.
     */
    private async handleEncryptedFrameReceived(sessionId: string, frame: EncryptedFrame): Promise<void> {
        if (sessionId !== this.currentSession?.sessionId) return;
        if (this.frameSource !== 'relay') return; // BUG-6: Respect P2P state
        if (!this.encryption.isSecure) {
            this.log('Received encrypted frame but encryption not established — dropping');
            return;
        }

        try {
            const decryptedData = this.encryption.decryptEncryptedFrame(frame);
            this._diag.framesReceived++;

            const frameData: FrameData = {
                data: decryptedData,
                width: frame.width,
                height: frame.height,
                codec: frame.codec as VideoCodec,
                isKeyFrame: frame.isKeyFrame,
                frameNumber: frame.frameNumber,
                timestamp: performance.now()
            };

            await this.videoDecoder.decodeFrame(frameData);
            this._diag.framesDecoded++;
        } catch (err) {
            this._diag.decodeErrors++;
            this._diag.lastError = String(err);
        }
    }

    private handleVideoStatsUpdate(stats: VideoStats): void {
        this.events.onVideoStatsUpdate?.(stats);
    }

    private handleNetworkStatsUpdate(stats: WebRTCStats): void {
        this.events.onNetworkStatsUpdate?.(stats);
    }

    private handleAuthStateChange(isAuthenticated: boolean): void {
        this.log(`Auth state: ${isAuthenticated ? 'authenticated' : 'not authenticated'}`);

        if (!isAuthenticated && this.signalR.isConnected) {
            // Token expired, disconnect
            this.disconnect();
        }
    }

    private handleError(error: Error): void {
        this.log(`Error: ${error.message}`);
        this.events.onError?.(error);
    }

    // =========================================================================
    // Private Methods - WebRTC
    // =========================================================================

    private async initializeWebRTC(sessionId: string): Promise<void> {
        // HIGH-2: Clean up existing WebRTC before creating new
        if (this.webRTC) {
            this.log('Cleaning up existing WebRTC instance before re-initialization');
            this.webRTC.close();
            this.webRTC = null;
            this.isP2PConnected = false;
            this.frameSource = 'relay';
        }

        // Fetch ICE servers (STUN/TURN) from server
        let webrtcConfig = {};
        try {
            const iceServersResponse = await this.signalR.getIceServers();
            if (iceServersResponse?.iceServers?.length > 0) {
                webrtcConfig = buildConfigFromIceServers(iceServersResponse);
                this.log(`Received ${iceServersResponse.iceServers.length} ICE servers (Provider: ${iceServersResponse.provider})`);
            }
        } catch (error) {
            this.log(`Failed to fetch ICE servers: ${error}, using defaults`);
        }

        this.webRTC = new WebRTCService(webrtcConfig, {
            onFrameReceived: async (frame) => {
                // Only switch to P2P mode when we actually receive a frame through DataChannel
                if (this.frameSource !== 'p2p') {
                    this.frameSource = 'p2p';
                    this.isP2PConnected = true;
                    this.log('First P2P frame received - switched to P2P mode');
                }
                await this.videoDecoder.decodeFrame(frame);
            },
            // WC-7: Route P2P audio frames to AudioService instead of dropping
            onAudioReceived: (data: Uint8Array) => {
                // M1: Detect codec from data — Opus packets start with specific TOC byte patterns,
                // while raw PCM is typically larger (48000Hz * 2ch * 2bytes * 20ms = 3840 bytes)
                const isLikelyOpus = data.length > 0 && data.length < 1280; // Opus max ~1275 bytes/frame
                this.audioService.processAudioFrame({
                    sessionId: this.currentSession?.sessionId || '',
                    sequenceNumber: 0,
                    timestamp: Date.now(),
                    codec: isLikelyOpus ? 1 : 0, // 0=PCM, 1=Opus — matches AudioCodec enum
                    data,
                    sampleRate: 48000,
                    channels: isLikelyOpus ? 2 : (data.length >= 3840 ? 2 : 1),
                    durationMs: 20
                });
            },
            onConnectionStateChange: (state) => {
                this.log(`WebRTC state: ${state}`);
            },
            onDataChannelOpen: () => {
                // DataChannel is open but don't switch frameSource yet —
                // wait until we actually receive a frame via P2P (in onFrameReceived above).
                // The Agent may still be sending via relay even after DataChannel opens.
                this.log('WebRTC DataChannel open, waiting for P2P frames...');
            },
            onDataChannelClose: () => {
                this.isP2PConnected = false;
                this.frameSource = 'relay';
                this.log('P2P disconnected - falling back to SignalR relay');
            },
            onIceCandidate: (candidate) => {
                // Send ICE candidate to agent via SignalR
                this.signalR.sendWebRTCSignal(sessionId, {
                    type: WebRTCSignalType.IceCandidate,
                    sessionId,
                    iceCandidate: candidate
                });
            },
            onStatsUpdate: (stats) => {
                this.events.onNetworkStatsUpdate?.(stats);
            },
            onLog: (message) => this.log(message)
        });

        // Perfect Negotiation: retry P2P with exponential backoff (max 2 attempts)
        const maxRetries = 2;
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                if (attempt > 0) {
                    const delay = 1000 * (1 << (attempt - 1)); // 1s, 2s
                    this.log(`WebRTC retry attempt ${attempt}/${maxRetries} after ${delay}ms`);
                    await new Promise(r => setTimeout(r, delay));

                    // Reinitialize on retry — close old and create fresh instance
                    this.webRTC?.close();
                    this.webRTC = new WebRTCService(webrtcConfig, {
                        onFrameReceived: async (frame) => {
                            if (this.frameSource !== 'p2p') {
                                this.frameSource = 'p2p';
                                this.isP2PConnected = true;
                                this.log('First P2P frame received - switched to P2P mode');
                            }
                            await this.videoDecoder.decodeFrame(frame);
                        },
                        onDataChannelClose: () => {
                            this.isP2PConnected = false;
                            this.frameSource = 'relay';
                            this.log('P2P disconnected - falling back to SignalR relay');
                        },
                        onIceCandidate: (candidate) => {
                            this.signalR.sendWebRTCSignal(sessionId, {
                                type: WebRTCSignalType.IceCandidate,
                                sessionId,
                                iceCandidate: candidate
                            });
                        },
                        onLog: (message) => this.log(message)
                    });
                }

                // Create offer
                const sdpOffer = await this.webRTC!.initializeAsInitiator();

                // Send offer to agent via SignalR
                await this.signalR.sendWebRTCSignal(sessionId, {
                    type: WebRTCSignalType.Offer,
                    sessionId,
                    sdp: sdpOffer,
                    sdpType: 'offer'
                });

                this.log('WebRTC offer sent');
                return; // Success
            } catch (error) {
                this.log(`WebRTC attempt ${attempt} failed: ${error}`);
                if (attempt >= maxRetries) {
                    this.log('WebRTC P2P failed after all retries — session continues with SignalR relay');
                    this.webRTC?.close();
                    this.webRTC = null;
                }
            }
        }
    }

    // =========================================================================
    // Private Methods - Utilities
    // =========================================================================

    private updateConnectionStatus(status: ConnectionStatus): void {
        this.connectionStatus = status;
        this.events.onConnectionStatusChange?.(status);
    }

    private detectPlatform(): string {
        const ua = navigator.userAgent;
        if (ua.includes('Windows')) return 'Windows';
        if (ua.includes('Mac')) return 'macOS';
        if (ua.includes('Linux')) return 'Linux';
        if (ua.includes('Android')) return 'Android';
        if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
        return 'Unknown';
    }

    private log(message: string): void {
        const timestamp = new Date().toISOString().substr(11, 12);
        const logMessage = `[${timestamp}] [Client] ${message}`;
        console.log(logMessage);
        this.events.onLog?.(logMessage);
    }
}
